import { Box } from '@chakra-ui/react';
import { cloneDeep, isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';
import { withTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { addAndSelectUser } from '../app-state/slices/ClientsSlice';
import { RootState } from '../app-state/store';
import * as ga from '../canopy-ui-components/google-analytics';
import FourZeroFour from '../components/Errors/404';
import Intercom from '../components/Intercom/Intercom';
import PageComponentRenderer from '../components/PageRendererHelpers/PageComponentRenderer';
import AppWrapper from '../components/Wrappers/AppWrapper';
import AuthWrapper from '../components/Wrappers/AuthWrapper';
import paths, { blockedPagesForGroupedClients } from '../page-config/paths';
const ResponsiveGridLayout = WidthProvider(Responsive);

function Index({ t }: any) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    let { pathname, search } = useLocation();
    let params = useParams();
    const { switchUser, user } = useSelector((state: RootState) => state.auth);
    const {
        clients,
        appData: {
            menuStructure: {
                data: { portfolio, sider, navBar },
            },
        },
    } = useSelector((state: RootState) => state);
    const [pageLayoutState, setPageLayoutState] = useState<Record<string, any>>(
        {}
    );
    const [pageConfig, setPageConfig] = useState<PageConfig | null>();
    const [layoutConfig, setLayoutConfig] = useState<Layouts>();
    const [currentSlug, setCurrentSlug] = useState<string>();
    const [clientsType, setClientsType] = useState<ClientsTypes>();

    useEffect(() => {
        if (!currentSlug || currentSlug !== pathname) {
            ga.send({ hitType: 'pageview', page: pathname });

            setCurrentSlug(pathname);
            if (pathname === '/') {
                navigate('/login');
                return;
            }

            if (pathname) {
                try {
                    const page = pathname;
                    let pageFile = pathname;
                    let config;
                    if (
                        pageFile.search('tableau-analytics') &&
                        params?.id &&
                        params?.view
                    ) {
                        pageFile = '/tableau-analytics';
                    }
                    if (pageFile) {
                        config = {
                            ...require(`../page-config${pageFile}.tsx`),
                        };
                    }

                    let {
                        pageConfig: _pageConfig,
                        layoutConfig: _layoutConfig,
                    } = cloneDeep(config);

                    if (_pageConfig?.components) {
                        _pageConfig.components = _pageConfig.components.map(
                            (item: PageComponent) => {
                                return {
                                    ...item,
                                    key: `${page}-${item?.key}`,
                                };
                            }
                        );

                        const header = _pageConfig.components.findIndex(
                            (item: PageComponent) =>
                                item.name.endsWith('header')
                        );
                        if (header !== -1) {
                            const headerComponent =
                                _pageConfig.components.splice(header, 1)[0];
                            _pageConfig.components.push(headerComponent);
                        }
                    }

                    for (let key in _layoutConfig) {
                        if (_layoutConfig[key]) {
                            _layoutConfig[key] = _layoutConfig?.[key].map(
                                (item: Layout) => {
                                    return {
                                        ...item,
                                        i: `${page}-${item?.i}`,
                                        static: true,
                                    };
                                }
                            );
                        }
                    }
                    const pageProps = _pageConfig?.pageProps;
                    let _pageLayoutState: Record<string, any> = {};
                    for (let key in pageProps) {
                        if (key === 'date' && pageProps[key] === 'today') {
                            _pageLayoutState.date = moment();
                            _pageLayoutState.maxDate = moment().startOf('day');
                        } else {
                            _pageLayoutState[key] = pageProps[key];
                        }
                    }
                    const clientsType: string = pathname?.split('/')?.[1];
                    if (
                        clientsType === 'portfolio' ||
                        clientsType === 'excel-addin' ||
                        clientsType === 'review-all'
                    ) {
                        clientsType && setClientsType(clientsType);
                    } else {
                        setClientsType(undefined);
                    }
                    if (!isEmpty(_pageLayoutState)) {
                        setPageLayoutState((pageLayoutState) => {
                            return {
                                user_id: pageLayoutState.user_id,
                                selectedUser: pageLayoutState.selectedUser,
                                ..._pageLayoutState,
                            };
                        });
                    }
                    setPageConfig(_pageConfig || null);
                    setLayoutConfig(_layoutConfig);
                } catch (err) {
                    setPageConfig(null);
                    console.log(err);
                }
            }
        }
    }, [pathname]);

    useEffect(() => {
        const queryParams: Record<string, any> = new URLSearchParams(search);
        setPageLayoutState((pageLayoutState: Record<string, any>) => {
            let params: any = {};
            for (const [key, value] of queryParams.entries()) {
                if (key === 'date') {
                    params = {
                        ...params,
                        maxDate: moment().startOf('day'),
                        date: moment(queryParams.get('date'), 'DD-MM-YYYY'),
                    };
                } else {
                    params[key] = value;
                }
            }
            return { ...(pageLayoutState || {}), ...params };
        });
    }, [search]);

    useEffect(() => {
        let _pageLayoutState: any = {
            user_id: null,
            selectedUser: null,
            per_child_user_ids: '',
        };
        if (user?.role === 'Customer') {
            const layoutStateUser = { ...user };
            _pageLayoutState = {
                user_id: layoutStateUser.id,
                selectedUser: {
                    ...layoutStateUser,
                    type: 'SINGLE_CLIENT',
                },
                loggedInUser: layoutStateUser,
            };
        } else if (switchUser?.role === 'Customer') {
            const layoutStateUser = { ...switchUser };
            _pageLayoutState = {
                user_id: layoutStateUser.id,
                selectedUser: {
                    ...layoutStateUser,
                    type: 'SINGLE_CLIENT',
                },
                loggedInUser: layoutStateUser,
            };
        } else if (clientsType && clients) {
            _pageLayoutState = {
                user_id: null,
                selectedUser: null,
                per_child_user_ids: '',
            };
            let selectedUser: any;
            if (clientsType === 'review-all') {
                selectedUser = {
                    id: 'all',
                    uniqueId: 'all',
                    username: 'all',
                    displayName: 'all',
                    meta: '',
                    type: 'GROUPED_CLIENT',
                    users: [],
                };
            } else {
                selectedUser = clients?.[clientsType]?.selectedUser;
            }

            _pageLayoutState = {
                user_id: selectedUser?.id || null,
                selectedUser: selectedUser || null,
                per_child_user_ids: '',
            };
        }

        if (switchUser) {
            _pageLayoutState = {
                ..._pageLayoutState,
                switch_user_id: switchUser?.id,
            };
        }

        setPageLayoutState((pageLayoutState) => {
            return {
                ...pageLayoutState,
                ..._pageLayoutState,
            };
        });
    }, [user, clients, clientsType, switchUser]);

    // In case of navigation from client list to allocations,
    // client list will set user on layout state and here it wi  be updated to the store
    useEffect(() => {
        if (pageLayoutState?.user) {
            dispatch(
                addAndSelectUser({
                    type: 'portfolio',
                    user: pageLayoutState?.user,
                })
            );
            setPageLayoutState((pageLayoutState) => ({
                ...pageLayoutState,
                user: null,
            }));
        }
    }, [pageLayoutState?.user]);

    useEffect(() => {
        const pageFile: string = pathname?.split('/')?.pop() || '';
        const loggedInUser = switchUser || user;

        if (
            !pageLayoutState?.user &&
            !clients?.portfolio?.selectedUser &&
            !pageLayoutState?.selectedUser &&
            (loggedInUser?.role === 'Partneradmin' ||
                loggedInUser?.role === 'RelationshipManager') &&
            pathname !== '/portfolio' &&
            pathname?.startsWith('/portfolio')
        ) {
            const { path } =
                sider?.find(({ key }: any) => key === 'portfolio') || {};
            path && navigate(path);
            return;
        }

        if (
            pageLayoutState?.selectedUser?.type === 'GROUPED_CLIENT' &&
            blockedPagesForGroupedClients?.includes(pageFile)
        ) {
            const { path } =
                portfolio?.find(
                    ({ key }: any) => key === 'portfolio-performance'
                ) || {};
            path && navigate(path);
            return;
        }
    }, [pageLayoutState?.selectedUser, pathname]);

    const getSelectedMenu = (pathname: any) => {
        let selectedMenu: any = {};
        selectedMenu =
            sider?.find(
                ({ path }: any) =>
                    pathname?.split('/')?.[1] === path?.split('/')?.[1]
            ) ||
            navBar?.find(
                ({ path }: any) =>
                    pathname?.split('/')?.[1] === path?.split('/')?.[1]
            );

        if (!selectedMenu) {
            navBar?.forEach((item: any) => {
                const foundItem = item?.subMenu?.find(
                    ({ path }: any) =>
                        pathname?.split('/')?.[1] === path?.split('/')?.[1]
                );
                if (foundItem) {
                    selectedMenu = foundItem;
                }
            });
        }
        return selectedMenu?.key;
    };
    return (
        <AuthWrapper>
            {
                <AppWrapper
                    selectedMenu={getSelectedMenu(pathname || '')}
                    type={clientsType}>
                    {pageConfig ? (
                        <>
                            <Intercom />
                            <Box width="100%">
                                <ResponsiveGridLayout
                                    className="layout"
                                    layouts={layoutConfig}
                                    rowHeight={30}
                                    breakpoints={{
                                        lg: 1200,
                                        md: 960,
                                        xs: 728,
                                        xxs: 320,
                                    }}
                                    margin={[2, 10]}
                                    cols={{ lg: 24, md: 24, xs: 24, xxs: 1 }}>
                                    {pageConfig?.components?.map(
                                        (component: PageComponent) => {
                                            return (
                                                <Box
                                                    width="100%"
                                                    height="full"
                                                    key={component.key}>
                                                    <PageComponentRenderer
                                                        key={component.key}
                                                        component={component}
                                                        pageLayoutState={
                                                            pageLayoutState
                                                        }
                                                        setPageLayoutState={
                                                            setPageLayoutState
                                                        }
                                                        t={t}
                                                        pageConfig={
                                                            pageConfig
                                                        }></PageComponentRenderer>
                                                </Box>
                                            );
                                        }
                                    )}
                                </ResponsiveGridLayout>
                            </Box>
                        </>
                    ) : (
                        <FourZeroFour t={t} />
                    )}
                </AppWrapper>
            }
        </AuthWrapper>
    );

    return null;
}

Index.propTypes = {
    t: PropTypes.func.isRequired,
};

export async function getStaticPaths() {
    return {
        paths: paths.map((item: string) => item.slice(0, -1)),
        fallback: false,
    };
}

export async function getStaticProps() {
    return { props: {} };
}

export default withTranslation('translations')(Index);
